import React, { useEffect, useState } from "react";
import { consoleApi } from "@/lib/console-auth";
import "@/styles/console.css";

/* ─────────────────────────────────────────────────────────────────────────────
   ConsoleInbox — minimal read-only Mira ticket inbox for Phase 1.
   Reuses the existing /api/admin/mira/tickets endpoint (Dipali's roles include
   'admin', so the guard passes). The marketing /admin keeps its own richer
   inbox view; this is the Console mirror.
   ───────────────────────────────────────────────────────────────────────── */

export default function ConsoleInbox() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            setError("");
            try {
                const { data } = await consoleApi.get("/admin/mira/tickets");
                setTickets(Array.isArray(data) ? data : []);
            } catch (e) {
                setError(e.response?.data?.detail || e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div data-testid="console-inbox">
            {loading ? (
                <p className="console-eyebrow" data-testid="console-inbox-loading">Loading tickets…</p>
            ) : error ? (
                <p style={{ color: "var(--console-danger)" }} data-testid="console-inbox-error">{error}</p>
            ) : tickets.length === 0 ? (
                <div className="console-card p-8 text-center" data-testid="console-inbox-empty">
                    <p style={{ color: "var(--console-text-muted)" }}>No tickets yet.</p>
                </div>
            ) : (
                <div className="console-card overflow-x-auto">
                    <table className="console-table" data-testid="console-inbox-table">
                        <thead>
                            <tr>
                                <th>Reference</th>
                                <th>Channel</th>
                                <th>Member</th>
                                <th>Tenant</th>
                                <th>Subject</th>
                                <th>Created</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tickets.slice(0, 50).map((t) => (
                                <tr key={t.id} data-testid={`inbox-row-${t.id}`}>
                                    <td style={{ fontFamily: "monospace", fontSize: 11 }}>{t.id}</td>
                                    <td><span className="console-pill console-pill-muted">{t.channel || "web"}</span></td>
                                    <td>
                                        <p style={{ fontSize: 13 }}>{t.lead_name || "—"}</p>
                                        <p className="console-eyebrow mt-[2px]">{t.lead_email || ""}</p>
                                    </td>
                                    <td><span className="console-pill console-pill-brass">{t.tenant_id || "—"}</span></td>
                                    <td>
                                        <p style={{ fontSize: 13, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {t.summary || t.subject || "—"}
                                        </p>
                                    </td>
                                    <td style={{ color: "var(--console-text-muted)", fontSize: 12 }}>
                                        {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
                                    </td>
                                    <td>
                                        <span className={`console-pill ${t.status === "new" ? "console-pill-active" : "console-pill-muted"}`}>
                                            {t.status || "new"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
